import React, { useEffect, useState } from 'react';
import systemTypeService from '../../services/systemTypeService';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/common/Toast';

const SystemTypesConfig = () => {
  const [systemTypes, setSystemTypes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newProblem, setNewProblem] = useState('');

  // Edit modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editTypeName, setEditTypeName] = useState('');
  const [editError, setEditError] = useState('');

  // Remove confirmation modal states
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [removeType, setRemoveType] = useState(null); // 'system' or 'problem'
  const [removeItem, setRemoveItem] = useState(null);

  // Restore defaults confirmation modal state
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  
  const { toasts, removeToast, success, error } = useToast();

  const selectedType = systemTypes.find(st => st.id === selectedId);

  useEffect(() => {
    load();
  }, []);

  const load = async (preferredId = null) => {
    setLoading(true);

    const result = await systemTypeService.getAll();

    if (result.success) {
      const list = result.data || [];
      setSystemTypes(list);

      if (list.length === 0) {
        setSelectedId(null);
      } else if (preferredId && list.some(st => st.id === preferredId)) {
        setSelectedId(preferredId);
      } else if (!selectedId || !list.some(st => st.id === selectedId)) {
        setSelectedId(list[0].id);
      }
    } else {
      error(result.error || 'Failed to load system types');
    }

    setLoading(false);
  };

  //  System Type Actions 
  const handleAddType = async () => {
    const name = newTypeName.trim();

    if (!name) {
      error('System type name cannot be empty');
      return;
    }

    setSaving(true);

    const result = await systemTypeService.create(name);

    if (result.success) {
      success(`"${name}" system type added successfully`);
      setNewTypeName('');
      await load(result.data.id);
    } else {
      error(result.error || 'Failed to add system type');
    }

    setSaving(false);
  };

  const openEditModal = (type) => {
    setEditingType(type);
    setEditTypeName(type.name);
    setEditError('');
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    if (saving) return;

    setIsEditModalOpen(false);
    setEditingType(null);
    setEditTypeName('');
    setEditError('');
  };

  const handleEditType = async () => {
    if (!editingType) return;

    const name = editTypeName.trim();

    if (!name) {
      setEditError('System type name cannot be empty');
      return;
    }

    if (name === editingType.name) {
      closeEditModal();
      return;
    }

    setSaving(true);

    const result = await systemTypeService.rename(editingType.id, name);

    if (result.success) {
      success(`"${editingType.name}" updated to "${name}"`);
      await load(editingType.id);
      closeEditModal();
    } else {
      setEditError(result.error || 'Failed to update system type');
    }

    setSaving(false);
  };

  // Problem Type Actions 

  const handleAddProblem = async () => {
    const problemName = newProblem.trim();

    if (!selectedId) {
      error('Please select a system type first');
      return;
    }

    if (!problemName) {
      error('Problem type name cannot be empty');
      return;
    }

    setSaving(true);

    const result = await systemTypeService.addProblem(selectedId, problemName);

    if (result.success) {
      success(`"${problemName}" problem type added successfully`);
      setNewProblem('');
      await load(selectedId);
    } else {
      error(result.error || 'Failed to add problem type');
    }

    setSaving(false);
  };

  //Remove Modal Actions

  const openRemoveSystemTypeModal = (type) => {
    setRemoveType('system');
    setRemoveItem(type);
    setIsRemoveModalOpen(true);
  };

  const openRemoveProblemTypeModal = (problem) => {
    setRemoveType('problem');
    setRemoveItem(problem);
    setIsRemoveModalOpen(true);
  };

  const closeRemoveModal = () => {
    if (saving) return;

    setIsRemoveModalOpen(false);
    setRemoveType(null);
    setRemoveItem(null);
  };

  const handleConfirmRemove = async () => {
    if (!removeType || !removeItem) return;

    setSaving(true);

    if (removeType === 'system') {
      const result = await systemTypeService.remove(removeItem.id);

      if (result.success) {
        success(`"${removeItem.name}" system type removed successfully`);
        await load();
        closeRemoveModal();
      } else {
        error(result.error || 'Failed to remove system type');
      }
    }

    if (removeType === 'problem') {
      const result = await systemTypeService.removeProblem(selectedId, removeItem.id);

      if (result.success) {
        success(`"${removeItem.name}" problem type removed successfully`);
        await load(selectedId);
        closeRemoveModal();
      } else {
        error(result.error || 'Failed to remove problem type');
      }
    }

    setSaving(false);
  };

  // Restore Defaults
  const openRestoreModal = () => {
    setIsRestoreModalOpen(true);
  };

  const closeRestoreModal = () => {
    if (saving) return;
    setIsRestoreModalOpen(false);
  };

  const handleReset = async () => {
    setSaving(true);

    const result = await systemTypeService.restoreDefaults();

    if (result.success) {
      success('Defaults restored successfully');
      await load(selectedId);
      closeRestoreModal();
    } else {
      error(result.error || 'Failed to restore defaults');
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        Loading...
      </div>
    );
  }

  const inputStyle = {
    flex: 1,
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid var(--border)',
    fontSize: '13px',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)'
  };

  return (
    <div>
      {toasts.map(t => (
        <Toast
          key={t.id}
          message={t.message}
          type={t.type}
          onClose={() => removeToast(t.id)}
        />
      ))}

      {/* Edit System Type Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        title="Edit System Type"
        size="small"
        footer={(
          <>
            <Button
              variant="secondary"
              onClick={closeEditModal}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              onClick={handleEditType}
              disabled={saving || !editTypeName.trim()}
            >
              {saving ? 'Updating...' : 'Update'}
            </Button>
          </>
        )}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label
            style={{
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--text-primary)'
            }}
          >
            System Type Name
          </label>

          <input
            value={editTypeName}
            onChange={e => {
              setEditTypeName(e.target.value);
              setEditError('');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleEditType();
              }
            }}
            placeholder="Enter system type name"
            style={inputStyle}
            disabled={saving}
            autoFocus
          />

          {editError && (
            <span style={{ color: '#ef4444', fontSize: '12px' }}>
              {editError}
            </span>
          )}
        </div>
      </Modal>

    {/* Restore Defaults Confirmation Modal */}
    <Modal
      isOpen={isRestoreModalOpen}
      onClose={closeRestoreModal}
      title="Restore Defaults"
      size="small"
      footer={(
        <>
          <Button
            variant="secondary"
            onClick={closeRestoreModal}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleReset}
            disabled={saving}
          >
            {saving ? 'Restoring...' : 'Restore'}
          </Button>
        </>
      )}
    >
      <div
        style={{
          fontSize: '14px',
          color: 'var(--text-primary)',
          lineHeight: '1.5'
        }}
      >
        <p style={{ margin: 0 }}>
          Do you want to restore default system types and problem types?
        </p>

        <p
          style={{
            margin: '10px 0 0',
            color: 'var(--text-secondary)',
            fontSize: '13px'
          }}
        >
          This will add missing default system types and problem types. Existing custom values will not be removed.
        </p>
      </div>
    </Modal>


      {/* Remove Confirmation Modal */}
      <Modal
        isOpen={isRemoveModalOpen}
        onClose={closeRemoveModal}
        title={removeType === 'system' ? 'Remove System Type' : 'Remove Problem Type'}
        size="small"
        footer={(
          <>
            <Button
              variant="secondary"
              onClick={closeRemoveModal}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              onClick={handleConfirmRemove}
              disabled={saving}
            >
              {saving ? 'Removing...' : 'Remove'}
            </Button>
          </>
        )}
      >
        <div
          style={{
            fontSize: '14px',
            color: 'var(--text-primary)',
            lineHeight: '1.5'
          }}
        >
          {removeType === 'system' && removeItem && (
            <p style={{ margin: 0 }}>
              Do you want to remove <strong>"{removeItem.name}"</strong> system type?
            </p>
          )}

          {removeType === 'problem' && removeItem && (
            <p style={{ margin: 0 }}>
              Do you want to remove <strong>"{removeItem.name}"</strong> problem type?
            </p>
          )}
        </div>
      </Modal>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}
      >
        <h2
          style={{
            fontSize: '18px',
            fontWeight: '700',
            color: 'var(--text-primary)',
            margin: 0
          }}
        >
          System Types & Problems
        </h2>

        <Button
          variant="secondary"
          onClick={openRestoreModal}
          disabled={saving}
        >
          Restore Defaults
        </Button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '380px 1fr',
          gap: '24px',
          minHeight: '500px'
        }}
      >
        {/* Left — System Types List */}
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--border)',
              fontWeight: '600',
              fontSize: '14px',
              background: 'var(--bg-secondary)'
            }}
          >
            System Types ({systemTypes.length})
          </div>

          <div style={{ overflowY: 'auto', maxHeight: '400px' }}>
            {systemTypes.length === 0 ? (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: '13px'
                }}
              >
                No system types defined yet
              </div>
            ) : (
              systemTypes.map(type => (
                <div
                  key={type.id}
                  onClick={() => setSelectedId(type.id)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto',
                    alignItems: 'center',
                    gap: '8px',
                    borderBottom: '1px solid var(--border)',
                    background: selectedId === type.id ? 'var(--primary-light)' : 'transparent',
                    color: selectedId === type.id ? 'var(--primary)' : 'var(--text-primary)',
                    fontSize: '13px'
                  }}
                >
                  <span
                    style={{
                      lineHeight: '1.35',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflow: 'visible',
                      fontWeight: selectedId === type.id ? '600' : '400'
                    }}
                  >
                    {type.name}
                  </span>

                  <Button
                    variant="secondary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(type);
                    }}
                    disabled={saving}
                  >
                    Edit
                  </Button>

                  <Button
                    variant="secondary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openRemoveSystemTypeModal(type);
                    }}
                    disabled={saving}
                  >
                    Remove
                  </Button>
                </div>
              ))
            )}
          </div>

          <div
            style={{
              padding: '12px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}
          >
            <input
              value={newTypeName}
              onChange={e => setNewTypeName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleAddType();
                }
              }}
              placeholder="New system type..."
              disabled={saving}
              style={inputStyle}
            />

            <Button
              variant="primary"
              onClick={handleAddType}
              disabled={!newTypeName.trim() || saving}
            >
              Add System Type
            </Button>
          </div>
        </div>

        {/* Right — Problems for selected type */}
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--border)',
              fontWeight: '600',
              fontSize: '14px',
              background: 'var(--bg-secondary)'
            }}
          >
            {selectedType
              ? `Problems — ${selectedType.name} (${selectedType.problems?.length || 0})`
              : 'Select a system type'}
          </div>

          {selectedType ? (
            <>
              <div style={{ overflowY: 'auto', maxHeight: '400px' }}>
                {(selectedType.problems || []).length === 0 ? (
                  <div
                    style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: 'var(--text-secondary)',
                      fontSize: '13px'
                    }}
                  >
                    No problems defined yet
                  </div>
                ) : (
                  selectedType.problems.map(problem => (
                    <div
                      key={problem.id}
                      style={{
                        padding: '10px 16px',
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                        gap: '10px',
                        alignItems: 'center',
                        borderBottom: '1px solid var(--border)',
                        fontSize: '13px'
                      }}
                    >
                      <span
                        style={{
                          minWidth: 0,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: '1.4'
                        }}
                      >
                        {problem.name}
                      </span>

                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => openRemoveProblemTypeModal(problem)}
                        disabled={saving}
                      >
                        Remove
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <div
                style={{
                  padding: '12px',
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  gap: '8px'
                }}
              >
                <input
                  value={newProblem}
                  onChange={e => setNewProblem(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleAddProblem();
                    }
                  }}
                  placeholder="Add new problem..."
                  disabled={saving}
                  style={inputStyle}
                />

                <Button
                  variant="primary"
                  onClick={handleAddProblem}
                  disabled={!newProblem.trim() || saving}
                >
                  Add Problem
                </Button>
              </div>
            </>
          ) : (
            <div
              style={{
                padding: '40px',
                textAlign: 'center',
                color: 'var(--text-secondary)'
              }}
            >
              Select a system type from the left
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemTypesConfig;